import React, { useEffect, useMemo, useState } from 'react';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage, openImageFileDialog } from '@/utils';
import { uploadHandoverPhoto } from '@/utils/handoverPhotoUpload';
import {
  ChecklistCatalog,
  HandoverChecklistItemInput,
  getHandoverChecklistCatalog,
} from '@/services/api/handovers';
import useFeatureFlag from '@/hooks/useFeatureFlag';

/**
 * Per-part handover checklist, driven by the backend catalog with a local
 * fallback: EQUIPMENT items are marked Ada / Tidak ada, CONDITION body panels
 * are photo slots, and the emitted `checklistItems` come with a resolved flag
 * the parent gates submission on.
 *
 * `web_fms_handover_checklist_require_photo` decides whether a photo counts
 * towards resolved; off, only the Ada / Tidak ada answer is demanded.
 */
const REQUIRE_PHOTO_FLAG = 'web_fms_handover_checklist_require_photo';

// Mirrors the backend catalog (nest-fleet-service
// shared/constants/handover-checklist.catalog.ts, version 1). Fallback only.
const FALLBACK_CATALOG: ChecklistCatalog = {
  version: 1,
  groups: [
    {
      code: 'spion',
      title: 'Spion',
      items: [
        eq('spion_kiri', 'Kaca Spion Kiri'),
        eq('spion_kanan', 'Kaca Spion Kanan'),
      ],
    },
    {
      code: 'kunci_remote',
      title: 'Kunci & Remote',
      items: [
        eq('kunci_utama', 'Kunci Utama'),
        eq('kunci_cadangan', 'Kunci Cadangan'),
        eq('remote_utama', 'Remote Utama'),
        eq('remote_cadangan', 'Remote Cadangan'),
      ],
    },
    {
      code: 'dokumen_plat',
      title: 'Dokumen & Plat',
      items: [
        eq('stnk', 'STNK'),
        eq('plat_depan', 'Plat Nomor Depan'),
        eq('plat_belakang', 'Plat Nomor Belakang'),
      ],
    },
    {
      code: 'kelistrikan',
      title: 'Kelistrikan',
      items: [eq('battery', 'Battery'), eq('charger', 'Charger')],
    },
    {
      code: 'kondisi_body',
      title: 'Kondisi Body',
      items: [
        cond('body_depan', 'Body Depan'),
        cond('body_samping_kiri', 'Body Samping Kiri'),
        cond('body_samping_kanan', 'Body Samping Kanan'),
        cond('body_belakang', 'Body Belakang'),
      ],
    },
  ],
};

function eq(code: string, label: string) {
  return { code, label, category: 'EQUIPMENT' as const, allowAbsent: true };
}
function cond(code: string, label: string) {
  return { code, label, category: 'CONDITION' as const, allowAbsent: false };
}

interface PartState {
  present?: boolean; // undefined = undecided (equipment); condition = always true
  photoUrl?: string;
  fileName?: string;
  uploading?: boolean;
}

interface Props {
  onChange: (items: HandoverChecklistItemInput[], isComplete: boolean) => void;
  isRequired?: boolean;
}

const HandoverPartChecklist: React.FC<Props> = ({
  onChange,
  isRequired = true,
}) => {
  const requirePhoto = useFeatureFlag(REQUIRE_PHOTO_FLAG, 'boolean');
  const [catalog, setCatalog] = useState<ChecklistCatalog>(FALLBACK_CATALOG);
  const [states, setStates] = useState<Record<string, PartState>>({});

  useEffect(() => {
    let alive = true;
    getHandoverChecklistCatalog()
      .then((c) => {
        if (alive && c?.groups?.length) setCatalog(c);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      alive = false;
    };
  }, []);

  const allItems = useMemo(
    () => catalog.groups.flatMap((g) => g.items),
    [catalog]
  );

  const isCondition = (code: string) =>
    allItems.find((i) => i.code === code)?.category === 'CONDITION';

  const partState = (code: string): PartState => states[code] ?? {};

  const isDone = (code: string): boolean => {
    const s = partState(code);
    // A body panel has nothing to answer but its photo.
    if (isCondition(code)) return requirePhoto ? !!s.photoUrl : true;
    // The Ada / Tidak ada answer is the record of what came with the unit, so no
    // flag makes it optional.
    if (s.present === undefined) return false;
    if (s.present === false) return true;
    return requirePhoto ? !!s.photoUrl : true;
  };

  // Emit the payload + completeness whenever anything changes.
  useEffect(() => {
    const items: HandoverChecklistItemInput[] = allItems.map((item) => {
      const s = partState(item.code);
      const present = item.category === 'CONDITION' ? true : s.present === true;
      return present
        ? { code: item.code, present: true, photoUrl: s.photoUrl }
        : { code: item.code, present: false };
    });
    const complete = allItems.every((i) => isDone(i.code));
    onChange(items, complete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, allItems, requirePhoto]);

  const setPresent = (code: string, present: boolean) => {
    setStates((prev) => {
      const next = { ...prev, [code]: { ...prev[code], present } };
      if (!present) {
        next[code] = { present: false }; // dropping presence drops the photo
      }
      return next;
    });
  };

  const uploadPhoto = (code: string) => {
    openImageFileDialog(async (file) => {
      if (!file) return;
      setStates((p) => ({ ...p, [code]: { ...p[code], uploading: true } }));
      try {
        const url = await uploadHandoverPhoto(file);
        setStates((p) => ({
          ...p,
          [code]: {
            ...p[code],
            present: isCondition(code) ? true : true,
            photoUrl: url,
            fileName: file.name,
            uploading: false,
          },
        }));
        showSuccessToast('Success', 'Upload image success');
      } catch (error) {
        setStates((p) => ({ ...p, [code]: { ...p[code], uploading: false } }));
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to upload image')
        );
      }
    });
  };

  const removePhoto = (code: string) => {
    setStates((p) => ({ ...p, [code]: { ...p[code], photoUrl: undefined } }));
  };

  const doneCount = allItems.filter((i) => isDone(i.code)).length;

  return (
    <div>
      <div className="px-4 py-2 text-sm font-medium text-gray-700 mb-1 ml-2 flex items-center justify-between">
        <span>
          Kelengkapan &amp; Kondisi Unit{' '}
          {isRequired && <span className="text-red-500">*</span>}
        </span>
        <span className="text-xs font-semibold text-gray-500">
          {doneCount}/{allItems.length}
        </span>
      </div>
      <p className="px-6 text-xs text-gray-500 mb-3">
        {requirePhoto
          ? 'Pilih Ada / Tidak ada tiap kelengkapan, lalu foto yang ada. Kondisi body wajib difoto.'
          : 'Pilih Ada / Tidak ada tiap kelengkapan. Foto opsional, tapi tetap membantu kalau ada perselisihan.'}
      </p>

      <div className="space-y-5 px-6">
        {catalog.groups.map((group) => (
          <div key={group.code}>
            <div className="text-xs font-bold text-gray-700 mb-2">
              {group.title}
            </div>
            <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
              {group.items.map((item) => (
                <PartRow
                  key={item.code}
                  label={item.label}
                  isCondition={item.category === 'CONDITION'}
                  state={partState(item.code)}
                  done={isDone(item.code)}
                  photoRequired={requirePhoto}
                  onSetPresent={(present) => setPresent(item.code, present)}
                  onUpload={() => uploadPhoto(item.code)}
                  onRemovePhoto={() => removePhoto(item.code)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PartRowProps {
  label: string;
  isCondition: boolean;
  state: PartState;
  done: boolean;
  photoRequired: boolean;
  onSetPresent: (present: boolean) => void;
  onUpload: () => void;
  onRemovePhoto: () => void;
}

const PartRow: React.FC<PartRowProps> = ({
  label,
  isCondition,
  state,
  done,
  photoRequired,
  onSetPresent,
  onUpload,
  onRemovePhoto,
}) => {
  const present = isCondition || state.present === true;
  const absent = !isCondition && state.present === false;
  const needsPhoto = photoRequired && present && !state.photoUrl;

  return (
    <div className="px-3 py-3">
      <div className="flex items-center gap-3">
        <StatusDot done={done} absent={absent} needsPhoto={needsPhoto} />
        <span className="flex-1 text-sm font-medium text-gray-800">
          {label}
        </span>
        {!isCondition && (
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs font-semibold">
            <button
              type="button"
              onClick={() => onSetPresent(true)}
              className={`px-3 py-1 ${
                state.present === true
                  ? 'bg-[#5E2AAC] text-white'
                  : 'bg-white text-gray-600'
              }`}
            >
              Ada
            </button>
            <button
              type="button"
              onClick={() => onSetPresent(false)}
              className={`px-3 py-1 border-l border-gray-200 ${
                state.present === false
                  ? 'bg-gray-500 text-white'
                  : 'bg-white text-gray-600'
              }`}
            >
              Tidak ada
            </button>
          </div>
        )}
      </div>

      {present && (
        <div className="mt-2 ml-8">
          {state.uploading ? (
            <span className="text-xs text-gray-500">Uploading…</span>
          ) : state.photoUrl ? (
            <div className="flex items-center gap-3">
              <span className="text-xs text-green-600 font-medium truncate max-w-[160px]">
                {state.fileName || 'Foto tersimpan'}
              </span>
              <button
                type="button"
                onClick={onUpload}
                className="text-xs font-semibold text-[#5E2AAC]"
              >
                Ganti
              </button>
              <button
                type="button"
                onClick={onRemovePhoto}
                className="text-xs font-semibold text-gray-500"
              >
                Hapus
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onUpload}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#5E2AAC]/40 bg-[#5E2AAC]/5 text-xs font-semibold text-[#5E2AAC]"
            >
              + Ambil foto{photoRequired ? '' : ' (opsional)'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const StatusDot: React.FC<{
  done: boolean;
  absent: boolean;
  needsPhoto: boolean;
}> = ({ done, absent, needsPhoto }) => {
  let cls = 'border-gray-300 bg-white';
  if (done && !absent) cls = 'border-green-500 bg-green-500';
  else if (absent) cls = 'border-gray-300 bg-gray-100';
  else if (needsPhoto) cls = 'border-amber-400 bg-white';
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${cls}`}
    >
      {done && !absent && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
      {absent && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="3">
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      )}
    </span>
  );
};

export default HandoverPartChecklist;
